/**
 * ลานประลอง (the duelling ground, docs/GDD.md): a walled stone ring where anyone may challenge someone standing
 * about it to a bout (ท้าประลอง). The bout is fought as the ladder's are (systems/arena.ts: the `Duel`, its
 * countdown and result), in the ring, until one falls to a tenth of their health; no one dies and nothing is lost,
 * and stepping out of the ring gives the bout away. The ladder and arena bouts of the zone are fought in the same
 * ring (the layout's `duel` point is its middle).
 *
 * Data only; the rules are `systems/duelGround.ts`. The ring is built in the zone's layout (data/layouts/yanyu2.ts
 * `DUEL_RING`).
 */
import { PLAINS_RING } from './plainsFill';
import { plains256, type ZoneId } from './zones';

export interface DuelGroundDef {
  readonly zone: ZoneId;
  readonly name: string;
  /** The ring's floor inside its wall [x0, x1, y0, y1) (metres): a bout is fought here. */
  readonly floor: readonly [number, number, number, number];
  /** The gate (outside the wall): the ring master stands beside it. */
  readonly gate: readonly [number, number];
  /** Where the two fighters are set down for the countdown. */
  readonly marks: readonly [readonly [number, number], readonly [number, number]];
  /** Where the ring's regulars (challengeable from the arena roster) stand watching, on the spectator steps. */
  readonly regulars: readonly { readonly roster: number; readonly at: readonly [number, number] }[];
}

/** The rules' numbers. */
export const DUEL_GROUND = {
  /** How near the ring's floor the challenger must stand (metres past its edge). */
  near: 8,
  /** How near the one challenged must be to the challenger (metres). */
  reach: 10,
  /** A bout ends when a fighter falls to this share of their health. */
  floorHp: 0.1,
  /** The chance a ring regular, or an offline body, takes the challenge up (the World's seeded draw). */
  acceptRegular: 0.8,
  acceptOffline: 0.6,
  /** Seconds before the same one can be asked again after saying no. */
  askAgain: 20,
} as const;

/** The new Yanyu's ring: on the east bank, east of the plaza's east gate, across the plaza bridge. */
export const DUEL_GROUNDS: readonly DuelGroundDef[] = [
  {
    zone: 'yanyu2',
    name: 'ลานประลองฝั่งตะวันออก',
    floor: [473, 507, 285, 315],
    gate: [469, 297.5],
    marks: [
      [482, 300],
      [498, 300],
    ],
    regulars: [
      { roster: 30, at: [488, 282.5] },
      { roster: 38, at: [494, 317.5] },
      { roster: 44, at: [509.5, 296] },
    ],
  },
];

/** The ring moved by a core offset (the town stamped in a region: region/core.ts), all its points. */
export const shiftDuelGround = (g: DuelGroundDef, o: { x: number; y: number }): DuelGroundDef =>
  !o.x && !o.y
    ? g
    : {
        ...g,
        floor: [g.floor[0] + o.x, g.floor[1] + o.x, g.floor[2] + o.y, g.floor[3] + o.y],
        gate: [g.gate[0] + o.x, g.gate[1] + o.y],
        marks: [
          [g.marks[0][0] + o.x, g.marks[0][1] + o.y],
          [g.marks[1][0] + o.x, g.marks[1][1] + o.y],
        ],
        regulars: g.regulars.map((r) => ({ ...r, at: [r.at[0] + o.x, r.at[1] + o.y] as const })),
      };

export const duelGroundIn = (zone: string): DuelGroundDef | null =>
  zone === 'plains' ? (plains256() ? PLAINS_RING : null) : (DUEL_GROUNDS.find((g) => g.zone === zone) ?? null);

/** Inside the ring's floor. */
export const inRing = (g: DuelGroundDef, at: { x: number; y: number }): boolean =>
  at.x >= g.floor[0] && at.x < g.floor[1] && at.y >= g.floor[2] && at.y < g.floor[3];

/** Within `DUEL_GROUND.near` of the ring's floor. */
export const nearRing = (g: DuelGroundDef, at: { x: number; y: number }): boolean => {
  const dx = Math.max(g.floor[0] - at.x, 0, at.x - g.floor[1]);
  const dy = Math.max(g.floor[2] - at.y, 0, at.y - g.floor[3]);
  return Math.hypot(dx, dy) <= DUEL_GROUND.near;
};

/** The ring's middle. */
export const ringCentre = (g: DuelGroundDef): { x: number; y: number } => ({
  x: (g.floor[0] + g.floor[1]) / 2,
  y: (g.floor[2] + g.floor[3]) / 2,
});
